// Lib
import { FC, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
// Hooks
import { useViewport } from "hooks";
// Types
import { UploadImageForm } from "./types";
// Components
import { Modal } from "components";
import { ImageUpload } from "components/Form";
// Styled
import { FlexContainer } from "styled/Box";
import { Button } from "styled/Buttons";

import { resolver } from "./validation";

interface ImageUploadModalProps {
  isLoading: boolean;
  open: boolean;
  handleClose: () => void;
  handleUpload: (data: { imageURL: string }) => void;
}

export const ImageUploadModal: FC<ImageUploadModalProps> = ({
  open,
  isLoading,
  handleUpload,
  handleClose,
}) => {
  const { isMobile } = useViewport();

  const {
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<UploadImageForm>({ resolver });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit: SubmitHandler<UploadImageForm> = async ({ image }) => {
    if (!image) {
      return;
    }

    handleUpload({ imageURL: image });
  };

  const onClose = () => {
    if (isLoading) return;

    handleClose();
    reset();
  };

  return (
    <Modal
      open={open}
      title="Upload Image"
      width={isMobile ? undefined : 420}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ImageUpload
          value={watch("image")}
          error={errors?.image?.message}
          onChange={image => setValue("image", image)}
          accept={["jpeg", "jpg", "png", "webp"]}
          maxSize={5 * 1024} // 5MB
          disabled={isLoading}
          style={{
            height: "auto",
            width: "100%",
            maxWidth: "400px",
            margin: "0 auto",
            aspectRatio: "1 / 1",
          }}
        />

        <FlexContainer
          $fullwidth
          $align="center"
          $justify="flex-end"
          $gap={8}
          $margin="24px 0 0"
          $column={isMobile}
        >
          <Button.Heading
            $fullWidth={isMobile}
            disabled={isLoading}
            onClick={onClose}
          >
            Cancel
          </Button.Heading>

          <Button.Heading
            $fullWidth={isMobile}
            loading={isLoading}
            type="primary"
            htmlType="submit"
          >
            Save
          </Button.Heading>
        </FlexContainer>
      </form>
    </Modal>
  );
};
